import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { checkboxClass, Field, FieldError, HelpTip, labelClass, NativeSelect } from "@/components/admin/cards";
import { DATE_FORMATS, DEFAULT_DATE_FORMAT } from "./bookingLink";
import {
  availablePlaceholders,
  builtinField,
  customFields,
  fieldLabel,
  impliedContactField,
  MAX_CUSTOM_FIELDS,
  MAX_FIELD_LABEL,
  RESERVATION_FIELD_LABELS,
  RESERVATION_FIELDS,
  RESERVATION_MODE_OPTIONS,
  RESERVATION_PRESETS,
  ReservationField,
  ReservationFieldSetting,
  ReservationPresetKey,
  ReservationType,
  ReservationTypeErrors,
  idFollowsLabel,
  idFromLabel,
  slugify,
  templatePlaceholders,
  unusedFields,
} from "./reservationTypes";

interface PresetPickerProps {
  suggested: ReservationPresetKey;
  onAdd: (preset: ReservationPresetKey) => void;
}

/** Picks a preset (suggested from the business type) for a new reservation type. */
export const ReservationPresetPicker = ({ suggested, onAdd }: PresetPickerProps) => {
  const [preset, setPreset] = useState<ReservationPresetKey | "">("");
  const value = preset || suggested;
  const suggestedName = RESERVATION_PRESETS.find((item) => item.key === suggested && item.key !== "custom")?.name;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Type of reservation" htmlFor="reservationPresetSelect" className="w-full sm:w-64">
          <NativeSelect id="reservationPresetSelect" value={value}
            onChange={(e) => setPreset(e.target.value as ReservationPresetKey)}>
            {RESERVATION_PRESETS.map((item) => (
              <option key={item.key} value={item.key}>
                {item.name}{item.key === suggested && item.key !== "custom" ? " (suggested)" : ""}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Button type="button" variant="outline" onClick={() => onAdd(value)}>
          <Plus aria-hidden="true" />Add reservation type
        </Button>
      </div>
      {suggestedName && (
        <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
          <Sparkles className="size-3.5" aria-hidden="true" />
          Suggested for your business: {suggestedName}
        </p>
      )}
    </div>
  );
};

const LABEL_ID_ERROR = "Use a button label with letters or numbers that no other reservation type uses.";

interface ReservationTypesEditorProps {
  types: ReservationType[];
  errors: ReservationTypeErrors[];
  onChange: (types: ReservationType[]) => void;
}

/** One settings card per reservation type. */
const ReservationTypesEditor = ({ types, errors, onChange }: ReservationTypesEditorProps) => {
  const update = (index: number, patch: Partial<ReservationType>) =>
    onChange(types.map((type, i) => (i === index ? { ...type, ...patch } : type)));

  const move = (index: number, offset: number) => {
    const next = [...types];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    onChange(next);
  };

  const changeLabel = (index: number, label: string) => {
    const otherIds = types.filter((_, i) => i !== index).map((type) => type.id.trim());
    // The hidden ID is made from the label; an older custom ID is kept.
    const followsLabel = idFollowsLabel(types[index], otherIds);
    update(index, { label, ...(followsLabel ? { id: idFromLabel(label, otherIds) } : {}) });
  };

  const builtin = (type: ReservationType, id: ReservationField) => type.fields.find((field) => !field.custom && field.id === id);

  const setBuiltin = (index: number, id: ReservationField, field: ReservationFieldSetting | null) => {
    const type = types[index];
    const others = type.fields.filter((item) => item.custom || item.id !== id);
    const builtins = RESERVATION_FIELDS.flatMap((key) => {
      if (key === id) return field ? [field] : [];
      const existing = builtin(type, key);
      return existing ? [existing] : [];
    });
    update(index, { fields: [...builtins, ...others.filter((item) => item.custom)] });
  };

  const setCustom = (index: number, customIndex: number, patch: Partial<ReservationFieldSetting> | null) => {
    const type = types[index];
    let seen = -1;
    const fields = type.fields.flatMap((field) => {
      if (!field.custom || ++seen !== customIndex) return [field];
      if (!patch) return [];
      const next = { ...field, ...patch };
      return [patch.label !== undefined ? { ...next, id: slugify(patch.label) } : next];
    });
    update(index, { fields });
  };

  const addCustom = (index: number) =>
    update(index, { fields: [...types[index].fields, { id: "", label: "", type: "text", required: true, custom: true }] });

  return (
    <div className="flex flex-col gap-3">
      {types.map((type, index) => {
        const err = errors[index] || {};
        const prefix = `reservationType${index}`;
        const title = type.label.trim() || `Reservation type ${index + 1}`;
        const used = templatePlaceholders(type.url_template);
        const unused = unusedFields(type);
        const custom = customFields(type);
        const implied = impliedContactField(type);
        const name = type.label || "reservation type";
        return (
          <fieldset key={index} className="flex min-w-0 flex-col gap-4 rounded-lg border border-border bg-card p-4" aria-label={title}>
            <div className="flex items-center justify-between gap-2">
              <strong className="text-sm font-semibold">{title}</strong>
              <div className="flex gap-1">
                <Button type="button" variant="outline" size="icon" className="size-8" aria-label={`Move ${name} up`}
                  disabled={index === 0} onClick={() => move(index, -1)}>
                  <ArrowUp aria-hidden="true" />
                </Button>
                <Button type="button" variant="outline" size="icon" className="size-8" aria-label={`Move ${name} down`}
                  disabled={index === types.length - 1} onClick={() => move(index, 1)}>
                  <ArrowDown aria-hidden="true" />
                </Button>
                <Button type="button" variant="outline" size="icon" className="size-8 text-destructive" aria-label={`Remove ${name}`}
                  onClick={() => onChange(types.filter((_, i) => i !== index))}>
                  <X aria-hidden="true" />
                </Button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Button label" htmlFor={`${prefix}Label`} required error={err.label || (err.id && LABEL_ID_ERROR)}>
                <Input id={`${prefix}Label`} placeholder="e.g. Book a table"
                  value={type.label} onChange={(e) => changeLabel(index, e.target.value)} />
              </Field>
              <Field label="How guests reserve" htmlFor={`${prefix}Mode`}>
                <NativeSelect id={`${prefix}Mode`} value={type.mode}
                  onChange={(e) => update(index, { mode: e.target.value as ReservationType["mode"] })}>
                  {RESERVATION_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </NativeSelect>
              </Field>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <span className={labelClass}>Details the assistant asks for</span>
                {implied && (
                  <HelpTip label="Details the assistant asks for">
                    The assistant still asks for {fieldLabel(implied).toLowerCase()} so you can reply.
                  </HelpTip>
                )}
              </div>
              <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
                {RESERVATION_FIELDS.map((id) => {
                  const field = builtin(type, id);
                  const label = RESERVATION_FIELD_LABELS[id];
                  return (
                    <div key={id} className="flex items-center gap-4 text-sm">
                      <span className="flex min-w-36 items-center gap-2">
                        <input id={`${prefix}Field_${id}`} type="checkbox" className={checkboxClass}
                          checked={Boolean(field)} onChange={(e) => setBuiltin(index, id, e.target.checked ? builtinField(id) : null)} />
                        <label htmlFor={`${prefix}Field_${id}`}>{label}</label>
                      </span>
                      <span className="flex items-center gap-2">
                        <input id={`${prefix}Field_${id}_required`} type="checkbox" className={checkboxClass}
                          aria-label={`${label} required`} disabled={!field} checked={Boolean(field?.required)}
                          onChange={(e) => field && setBuiltin(index, id, { ...field, required: e.target.checked })} />
                        <label className="text-muted-foreground" htmlFor={`${prefix}Field_${id}_required`} aria-hidden="true">Required</label>
                      </span>
                    </div>
                  );
                })}
              </div>
              <FieldError message={err.fields} />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <span className={labelClass}>Extra details</span>
                <HelpTip label="Extra details">Up to {MAX_CUSTOM_FIELDS} more things to ask, such as a preferred doctor.</HelpTip>
              </div>
              {custom.map((field, customIndex) => (
                <div key={customIndex} className="flex items-center gap-3">
                  <Input className="max-w-80" maxLength={MAX_FIELD_LABEL}
                    aria-label={`Extra detail ${customIndex + 1} name`} placeholder="e.g. Preferred doctor"
                    value={field.label || ""} onChange={(e) => setCustom(index, customIndex, { label: e.target.value })} />
                  <span className="flex items-center gap-2 text-sm">
                    <input id={`${prefix}Custom${customIndex}Required`} type="checkbox" className={checkboxClass}
                      aria-label={`Extra detail ${customIndex + 1} required`} checked={field.required} onChange={(e) => setCustom(index, customIndex, { required: e.target.checked })} />
                    <label htmlFor={`${prefix}Custom${customIndex}Required`} aria-hidden="true">Required</label>
                  </span>
                  <Button type="button" variant="outline" size="icon" className="size-8 text-destructive"
                    aria-label={`Remove extra detail ${field.label || customIndex + 1}`} onClick={() => setCustom(index, customIndex, null)}>
                    <X aria-hidden="true" />
                  </Button>
                </div>
              ))}
              {custom.length < MAX_CUSTOM_FIELDS && (
                <div>
                  <Button type="button" variant="outline" size="sm" onClick={() => addCustom(index)}>
                    <Plus aria-hidden="true" />Add extra detail
                  </Button>
                </div>
              )}
              <FieldError message={err.custom_fields} />
            </div>

            {type.mode === "link_out" ? (
              <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
                <div className="flex min-w-0 flex-col gap-1.5">
                  <Field label="Booking URL" htmlFor={`${prefix}Url`} required error={err.url_template}
                    hint={<>
                      Placeholders: {availablePlaceholders(type).map((p) => `{${p}}`).join(" ")}
                      {used.length > 0 && <> · Used: {used.map((p) => `{${p}}`).join(" ")}</>}
                    </>}>
                    <Input id={`${prefix}Url`}
                      placeholder="https://restaurant.example/book?date={date}&guests={party_size}"
                      value={type.url_template || ""} onChange={(e) => update(index, { url_template: e.target.value })} />
                  </Field>
                  {!err.url_template && unused.length > 0 && (
                    <p className="text-sm text-warning-700" role="status">
                      Not passed to the booking URL: {unused.map(fieldLabel).join(", ")}.
                    </p>
                  )}
                </div>
                <Field label="Date format" htmlFor={`${prefix}DateFormat`}>
                  <NativeSelect id={`${prefix}DateFormat`} value={type.date_format || DEFAULT_DATE_FORMAT}
                    onChange={(e) => update(index, { date_format: e.target.value })}>
                    {DATE_FORMATS.map((format) => <option key={format} value={format}>{format}</option>)}
                  </NativeSelect>
                </Field>
              </div>
            ) : (
              <Field label="Enquiry email" htmlFor={`${prefix}Email`} required error={err.enquiry_email}
                hint="Where the guest's request is sent." className="md:max-w-md">
                <Input id={`${prefix}Email`} type="email" placeholder="reservations@your-property.com"
                  value={type.enquiry_email || ""} onChange={(e) => update(index, { enquiry_email: e.target.value })} />
              </Field>
            )}
          </fieldset>
        );
      })}
    </div>
  );
};

export default ReservationTypesEditor;
